import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Box,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useLoginForm } from '../../hooks/usePage';
import PasswordResetForm from './PasswordResetForm';
import EmailLoginForm from './EmailLoginForm';
import SocialLoginButtons from './SocialLoginButtons';
import LoginDialogActions from './LoginDialogActions';

const LoginDialog = ({
  open,
  onClose,
  email,
  setEmail,
  password,
  setPassword,
  isSignup,
  setIsSignup,
  handleEmailAuth,
  handleGoogleAuth,
  handleKakaoAuth
}) => {
  const { t } = useTranslation();
  const { 
    handleNaverAuth,
    resetPasswordMode,
    setResetPasswordMode,
    resetPasswordEmail,
    setResetPasswordEmail,
    resetPasswordSuccess,
    resetPasswordError,
    handlePasswordReset,
    authError,
    setAuthError
  } = useAuth();

  // 디버깅: authError 상태 변화 추적
  useEffect(() => {
    console.log('🔍 [LoginDialog] authError 상태 변화:', authError);
  }, [authError]);

  // 분리된 커스텀 훅 사용
  const loginForm = useLoginForm(
    isSignup, 
    email, 
    setEmail, 
    password, 
    setPassword, 
    handleEmailAuth, 
    onClose,
    setAuthError
  );

  const handleSocialLogin = async (provider) => {
    console.log(`🔐 [LoginDialog] ${provider} 로그인 시작`);
    
    let success;
    switch (provider) {
      case 'google':
        success = await handleGoogleAuth();
        break;
      case 'kakao':
        success = await handleKakaoAuth();
        break;
      case 'naver':
        success = await handleNaverAuth();
        break;
      default:
        return;
    }
    
    console.log(`🔐 [LoginDialog] ${provider} 로그인 결과:`, success);
    // 모달 닫기는 각 소셜 로그인 함수에서 처리
  };

  const handleClose = () => {
    // 완전한 초기화
    setEmail('');
    setPassword('');
    setIsSignup(false); // 로그인 모드로 리셋
    loginForm.resetFormState();
    setResetPasswordMode(false);
    setResetPasswordEmail('');
    setAuthError(''); // 에러도 초기화
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 0 20px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {resetPasswordMode ? t('auth.passwordReset') : (isSignup ? t('auth.signup') : t('auth.login'))}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: '#666',
            '&:hover': {
              color: '#1976d2',
              bgcolor: 'rgba(25, 118, 210, 0.08)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {resetPasswordMode ? (
          <PasswordResetForm
            resetPasswordEmail={resetPasswordEmail}
            setResetPasswordEmail={setResetPasswordEmail}
            resetPasswordSuccess={resetPasswordSuccess}
            resetPasswordError={resetPasswordError}
            handlePasswordReset={handlePasswordReset}
            emailError={loginForm.emailError}
            setEmailError={loginForm.setEmailError}
          />
        ) : (
          <>
            <EmailLoginForm
              isSignup={isSignup}
              email={email}
              password={password}
              confirmPassword={loginForm.confirmPassword}
              emailError={loginForm.emailError}
              passwordError={loginForm.passwordError}
              authError={authError}
              localError={loginForm.localError}
              handleEmailChange={loginForm.handleEmailChange}
              handlePasswordChange={loginForm.handlePasswordChange}
              handleConfirmPasswordChange={loginForm.handleConfirmPasswordChange}
              handleEmailSubmit={loginForm.handleEmailSubmit}
              setLocalError={loginForm.setLocalError}
            />

            <SocialLoginButtons
              handleSocialLogin={handleSocialLogin}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <LoginDialogActions
          resetPasswordMode={resetPasswordMode}
          setResetPasswordMode={setResetPasswordMode}
          setResetPasswordEmail={setResetPasswordEmail}
          setIsSignup={setIsSignup}
          setPassword={setPassword}
          isSignup={isSignup}
          resetFormState={loginForm.resetFormState}
        />
      </DialogActions>
    </Dialog>
  );
};

export default LoginDialog; 